import { useState, useEffect, useCallback } from 'react';
import {
  tickerSentimentService,
  TickerSentiment,
} from '@/services/analytics/tickerSentimentService';

interface UseTickerSentimentReturn {
  sentiment: TickerSentiment | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching ticker sentiment within a group
 * Analyzes messages mentioning the ticker to determine group sentiment (bullish/bearish/neutral)
 *
 * @param groupId - Group ID to analyze
 * @param ticker - Stock ticker symbol
 * @param timeframe - Time window ("24h", "7d", "30d")
 * @param includeAI - Whether to include AI messages in sentiment calculation (default: true)
 * @returns Sentiment data, loading state, error, and refetch function
 */
export const useTickerSentiment = (
  groupId: string | undefined,
  ticker: string,
  timeframe: '24h' | '7d' | '30d' = '7d',
  includeAI: boolean = true
): UseTickerSentimentReturn => {
  const [sentiment, setSentiment] = useState<TickerSentiment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSentiment = useCallback(async () => {
    // If no groupId, skip sentiment analysis (global stock view, not group-specific)
    if (!groupId) {
      console.log(`[useTickerSentiment] No groupId provided, skipping sentiment analysis`);
      setSentiment(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log(
        `[useTickerSentiment] Fetching sentiment for ${ticker} in group ${groupId} (includeAI: ${includeAI})`
      );

      const { data, error: serviceError } = await tickerSentimentService.getTickerSentiment(
        groupId,
        ticker,
        timeframe,
        includeAI
      );

      if (serviceError) {
        throw new Error(serviceError);
      }

      setSentiment(data);
      console.log(
        `[useTickerSentiment] ✅ Sentiment for ${ticker}:`,
        data?.sentiment,
        `(${data?.totalMentions} mentions)`
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ticker sentiment';
      setError(errorMessage);
      console.error(`[useTickerSentiment] Error for ${ticker}:`, errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [groupId, ticker, timeframe, includeAI]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchSentiment();
  }, [fetchSentiment]);

  return {
    sentiment,
    isLoading,
    error,
    refetch: fetchSentiment,
  };
};
